import React from "react"
import styled from '@mui/material/styles/styled'
import { ExpandMore } from '@mui/icons-material'
import InputAdornment from '@mui/material/InputAdornment'
import { Badge, Text, TextField } from 'components'
import { Autocomplete, AutocompleteProps } from 'components/Autocomplete'

export interface Option { label: string, id: string, todoCount?: number }

export type LocationSelectorProps = Partial<Omit<AutocompleteProps<Option, false, true, false>, 'onChange'>> & { label: string, options: Option[], selected: Option, onChange: (value: Option) => void, adornmentIcon: React.ReactElement }

export const StyledAutocomplete = styled(Autocomplete, {
  // shouldForwardProp: (prop) => true,
})<AutocompleteProps<Option, false, true, false>>(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    paddingTop: '6px',
    paddingBottom: '6px',
    cursor: 'pointer',

    '& .MuiOutlinedInput-input': {
      paddingTop: '4px',
      paddingBottom: '4px',
      cursor: 'pointer',
      fontWeight: '500',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
      borderWidth: 0,
      marginTop: '5px',
      '& legend': {
        display: 'none',
      },
    },

    '&.Mui-focused': {
      borderWidth: '1px',
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.background.default,

      '& .MuiOutlinedInput-input': {
        cursor: 'text',
      },
    },

    '&.Mui-focused:hover': {
      borderWidth: '1px',
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.background.default,
    },

    '&:hover': {
      borderWidth: 0,
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.grey[100],
    },
  },

  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    borderColor: theme.palette.divider,
  },

  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderWidth: 0,
    borderColor: theme.palette.divider,
  },

  '& .MuiOutlinedInput-root.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    borderColor: theme.palette.divider,
  },

  '& .MuiAutocomplete-endAdornment .MuiButtonBase-root:hover': {
    backgroundColor: 'transparent',
  },

  '& .MuiFormLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    transform: 'none',
    position: 'relative',
    marginBottom: theme.spacing(1),
  },
}))

export const LocationSelector: React.FC<LocationSelectorProps> = ({ label, options, selected, onChange, adornmentIcon, ...autocompleteProps }) => {

  return <StyledAutocomplete
    {...autocompleteProps}
    disablePortal
    disableClearable
    options={options || null}
    defaultValue={selected}
    value={selected}
    sx={{ width: '100%' }}
    popupIcon={<ExpandMore />}
    slotProps={{
      popupIndicator: {
        disableRipple: true,
      }
    }}
    renderInput={(params) =>
      <TextField
        {...params}
        label={label}
        InputProps={{
          ...params.InputProps,
          // sx: { fontWeight: 'bold' },
          startAdornment: (
            <InputAdornment position="start" sx={(theme) => ({ pl: 1, mr: 0.5, color: theme.palette.primary.main })}>
              { adornmentIcon }
            </InputAdornment>
          ),
        }}
      />
    }
    renderOption={(props, option) => {
      return <li {...props} key={option.id}>
        { !!(option.todoCount && option.todoCount > 0) && <Badge badgeContent={option.todoCount} color="error" variant="dot" anchorOrigin={{ vertical: 'top', horizontal: 'left'}} sx={{ width: '100%' }}>
          { adornmentIcon }
          <Text whiteSpace="break-spaces">{ option.label }</Text>
        </Badge> }
        { !(option.todoCount && option.todoCount > 0) && <>
          { adornmentIcon }
          <Text whiteSpace="break-spaces">{ option.label }</Text>
        </> }
      </li>
    }}
    isOptionEqualToValue={(option, value) => option.id === value?.id}
    onChange={(_e, value: Option) => onChange(value)}
    ListboxProps={{ sx: {
      '& .MuiAutocomplete-option': {
        pl: 2,
        '& .MuiTypography-root': {
          ml: 1.5,
        },
      },
    } }}
  />
}

export default LocationSelector
